import React, { useState } from "react";
import { Check, ArrowRight } from "lucide-react";

const plans = [
  {
    name: "Starter",
    price: { m: 49, y: 39 },
    blurb: "For teams launching their first AI agent.",
    features: [
      "Up to 2,000 conversations / mo",
      "2 AI agents, 1 knowledge base",
      "Web chat + email channel",
      "Community support",
      "7-day conversation history",
    ],
    cta: "Start free trial",
    highlight: false,
  },
  {
    name: "Growth",
    price: { m: 249, y: 199 },
    blurb: "The favorite of scaling support teams.",
    features: [
      "Up to 25,000 conversations / mo",
      "Unlimited agents & KBs",
      "All channels (WhatsApp, voice, IG, Slack…)",
      "Visual flow builder + versioning",
      "Role-based access & audit logs",
      "Priority support (4h SLA)",
    ],
    cta: "Request demo",
    highlight: true,
    badge: "Most loved",
  },
  {
    name: "Enterprise",
    price: { m: "Custom", y: "Custom" },
    blurb: "For regulated, global teams with scale.",
    features: [
      "Unlimited conversations",
      "Dedicated deployment + VPC option",
      "SSO (SAML), SCIM, custom DPA",
      "EU / US / India data residency",
      "Solutions architect + CSM",
      "99.99% uptime SLA",
    ],
    cta: "Talk to sales",
    highlight: false,
  },
];

export default function Pricing({ onDemoClick }) {
  const [yearly, setYearly] = useState(true);

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-28 md:py-40 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col items-center text-center mb-14">
          <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-[#7C5CFF] mb-4">
            ── Pricing
          </span>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-white max-w-3xl">
            Honest pricing.{" "}
            <span className="italic">Outcome-aligned.</span>
          </h2>
          <p className="mt-5 text-white/55 text-[16px] max-w-xl">
            Pay for resolved conversations, not seats. Cancel, switch or scale
            any time — no awkward sales calls.
          </p>

          <div
            className="mt-8 inline-flex items-center bg-white/[0.04] border border-white/10 rounded-full p-1"
            data-testid="pricing-toggle"
          >
            <button
              onClick={() => setYearly(false)}
              className={`px-4 py-1.5 text-sm rounded-full transition-colors ${
                !yearly ? "bg-white text-[#0a0a10]" : "text-white/70"
              }`}
              data-testid="pricing-toggle-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setYearly(true)}
              className={`px-4 py-1.5 text-sm rounded-full transition-colors inline-flex items-center gap-2 ${
                yearly ? "bg-white text-[#0a0a10]" : "text-white/70"
              }`}
              data-testid="pricing-toggle-yearly"
            >
              Yearly
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  yearly
                    ? "bg-[#3DDFB3]/20 text-[#157e5a]"
                    : "bg-[#3DDFB3]/20 text-[#3DDFB3]"
                }`}
              >
                -20%
              </span>
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {plans.map((p, i) => (
            <div
              key={i}
              data-testid={`pricing-card-${p.name.toLowerCase()}`}
              className={`p-8 md:p-9 rounded-2xl relative ${
                p.highlight
                  ? "grad-border"
                  : "card-surface"
              }`}
            >
              {p.badge && (
                <span className="absolute -top-3 left-8 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] font-mono bg-[#7C5CFF] text-white rounded-full shadow-[0_0_20px_rgba(124,92,255,0.5)]">
                  {p.badge}
                </span>
              )}
              <div className="font-serif text-[28px] text-white mb-1">
                {p.name}
              </div>
              <p className="text-white/55 text-[14px] mb-7">{p.blurb}</p>

              <div className="flex items-baseline gap-2 mb-1">
                {typeof p.price.m === "number" ? (
                  <>
                    <span className="font-serif text-5xl text-white">
                      ${yearly ? p.price.y : p.price.m}
                    </span>
                    <span className="text-white/50 text-sm">/ month</span>
                  </>
                ) : (
                  <span className="font-serif text-5xl text-white">
                    Custom
                  </span>
                )}
              </div>
              <div className="text-[11px] font-mono text-white/40 uppercase tracking-[0.12em] mb-7">
                {yearly ? "Billed annually" : "Billed monthly"}
              </div>

              <button
                onClick={onDemoClick}
                data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-full text-[14px] font-medium transition-all ${
                  p.highlight
                    ? "btn-primary text-white"
                    : "btn-ghost text-white"
                }`}
              >
                {p.cta}
                <ArrowRight className="w-4 h-4" />
              </button>

              <ul className="mt-8 space-y-3">
                {p.features.map((f, j) => (
                  <li
                    key={j}
                    className="flex items-start gap-2.5 text-[14px] text-white/75"
                  >
                    <Check
                      className="w-4 h-4 mt-0.5 flex-shrink-0"
                      style={{
                        color: p.highlight ? "#3DDFB3" : "#7C5CFF",
                      }}
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
